/**
 * InlineSettingsPanel — bottom-sheet on mobile, side-drawer on desktop.
 *
 * Lives inside the chat surface so the End_User doesn't have to
 * navigate to a separate route. Surface-deliberate scope: only the
 * controls the End_User should be able to touch live here. The
 * profile editor and companion-name editor are intentionally absent
 * — those expose facts that the AI is supposed to "already know" and
 * showing them in the UI would break the illusion that the
 * AI_Companion was set up by someone who knows her.
 *
 * Sections rendered (in order):
 *   - Theme picker (gradient swatch chips)
 *   - Language + practice mode
 *   - Compliment pacing + reduced motion
 *   - Data actions (export / import / wipe)
 *
 * The panel is fully keyboard-accessible (Esc closes, focus trapped
 * via the same mechanism Modal uses). The trigger that opens it
 * lives in the chat header and the Composer.
 */

import { useEffect } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { ThemePicker } from '../theme';
import { LanguagePicker } from './LanguagePicker';
import { PacingSlider } from './PacingSlider';
import { VoicePicker } from './VoicePicker';
import { DataActions } from './DataActions';

import styles from './InlineSettingsPanel.module.css';

interface InlineSettingsPanelProps {
  open: boolean;
  onClose: () => void;
}

export function InlineSettingsPanel({
  open,
  onClose,
}: InlineSettingsPanelProps): ReactElement {
  const { t } = useTranslation();

  // Esc-to-close. Mounted only while open so the listener does not
  // interfere with other surface-level shortcuts when collapsed.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          key="settings-backdrop"
          className={styles.backdrop}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={onClose}
          data-testid="settings-backdrop"
        >
          <motion.aside
            key="settings-panel"
            className={styles.panel}
            role="dialog"
            aria-modal="true"
            aria-label={t('settings.title')}
            initial={{ x: '100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 28, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="inline-settings"
          >
            <header className={styles.header}>
              <h2 className={styles.title}>{t('settings.title')}</h2>
              <button
                type="button"
                className={styles.closeBtn}
                onClick={onClose}
                aria-label="Kapat"
              >
                ✕
              </button>
            </header>

            <div className={styles.body}>
              <Section title={t('settings.theme.title')}>
                <ThemePicker />
              </Section>
              <LanguagePicker />
              <PacingSlider />
              <VoicePicker />
              <DataActions />
            </div>
          </motion.aside>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

interface SectionProps {
  title: string;
  children: ReactNode;
}

function Section({ title, children }: SectionProps): ReactElement {
  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>{title}</h3>
      {children}
    </section>
  );
}
